import { BiomeSource, FixedBiomeSource, Identifier, StructureSet, Climate, DensityFunction, Holder, WorldgenRegistries, NoiseParameters, HolderSet, WorldgenStructure, Json, NoiseGeneratorSettings, RandomState, NoiseSettings, LevelHeight } from "deepslate";
import { ResourceLocation } from "mc-datapack-loader";
import { defineStore } from "pinia";
import { compile, computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { VANILLA_ITEMS } from "../BuildIn/VanillaItems.js";
import { getCustomDensityFunction, hashCode, versionMetadata } from "../util.js";
import { useDatapackStore } from "./useDatapackStore.js";
import { useSettingsStore } from "./useSettingsStore.js";

import messages from '@intlify/unplugin-vue-i18n/messages'

const VANILLA_ITEM_SET = new Set(VANILLA_ITEMS)
const FALLBACK_STRUCTURE_ICON = "stone_bricks"

// Must mirror the biome ids returned by deepslate's TheEndBiomeSource
const END_BIOMES = ["the_end", "end_highlands", "end_midlands", "small_end_islands", "end_barrens"]

function qualifiedBiomeId(value: unknown): string | undefined {
    const id = Json.readString(value)
    if (id === undefined) return undefined
    return id.indexOf(":") === -1 ? "minecraft:" + id : id
}

/**
 * The biomes the loaded dimension can generate, derived from the same json that
 * BiomeSource.fromJson uses. Returns an empty set if the source is unknown.
 */
function collectPossibleBiomes(biome_source_json: {[x: string]: unknown} | undefined): Set<string> {
    const biomes = new Set<string>()
    if (biome_source_json === undefined) return biomes

    const type = Json.readString(biome_source_json.type)?.replace(/^minecraft:/, "")
    const addBiomeList = (value: unknown) => {
        if (!Array.isArray(value)) return
        for (const entry of value) {
            const biome = qualifiedBiomeId(Json.readObject(entry)?.biome ?? entry)
            if (biome !== undefined) biomes.add(biome)
        }
    }

    if (type === "multi_noise") {
        addBiomeList(biome_source_json.biomes)
    } else if (type === "checkerboard") {
        addBiomeList(biome_source_json.biomes)
        const biome = qualifiedBiomeId(biome_source_json.biome)
        if (biome !== undefined) biomes.add(biome)
    } else if (type === "fixed") {
        const biome = qualifiedBiomeId(biome_source_json.biome)
        if (biome !== undefined) biomes.add(biome)
    } else if (type === "the_end") {
        for (const biome of END_BIOMES) biomes.add("minecraft:" + biome)
    }

    return biomes
}

type StructureWithSettings = { settings?: { validBiomes?: HolderSet<unknown> } }

/** The biomes a structure is allowed to generate in, or undefined if unknown. */
function structureBiomeIds(structure: WorldgenStructure): Set<string> | undefined {
    const valid_biomes = (structure as unknown as StructureWithSettings).settings?.validBiomes
    if (valid_biomes === undefined) return undefined

    const biomes = new Set<string>()
    for (const holder of valid_biomes.getEntries()) {
        const key = holder.key()?.toString()
        if (key !== undefined) biomes.add(key)
    }
    return biomes
}

/**
 * A structure is only listed for dimensions whose biomes intersect the
 * structure's biome list. Structures with unresolvable biomes are kept.
 */
function canGenerateInDimension(structure: WorldgenStructure, possible_biomes: Set<string>): boolean {
    if (possible_biomes.size === 0) return true

    const biomes = structureBiomeIds(structure)
    if (biomes === undefined || biomes.size === 0) return true

    for (const biome of biomes) {
        if (possible_biomes.has(biome)) return true
    }
    return false
}

export type LoadedDimension = {
    structure_icons?: Map<string, Identifier>,
    hidden_structures?: Set<string>
    /// Structures that can generate in the loaded dimension (used to filter the structure lists)
    available_structures?: Set<string>
    /// Biomes that the loaded dimension can generate
    possible_biomes?: Set<string>
    level_height?: LevelHeight,
    noise_settings_id?: Identifier,
    noise_settings_json?: {[x: string]: unknown},
    biome_source_json?: {[x: string]: unknown},
}

export const useLoadedDimensionStore = defineStore('loaded_dimension', () => {

    const datapackStore = useDatapackStore()
    const settingsStore = useSettingsStore()
    const i18n = useI18n()

    const loaded_dimension = reactive<LoadedDimension>({})
    var biome_source: BiomeSource | undefined = undefined
    var biome_colors: Map<string, { r: number, g: number, b: number } > = new Map<string, { r: number, g: number, b: number}>()

    var default_messages: {[key: string]: string} = {}

    datapackStore.$subscribe((mutation, state) => {
        reload()
    })

    watch(() => settingsStore.mc_version, reload)
    watch(() => settingsStore.world_preset, reload)
    watch(() => settingsStore.dimension, reload)
    watch(() => settingsStore.seed, reload)
    
    function handle_biome_colors(namespace:string, json: {[key: string]: { r: number, g: number, b: number, name?: string }}){
        for (const biome in json) {
            const biome_id = biome.indexOf(":") === -1 ? namespace + ":" + biome : biome

            const entry = json[biome]
            biome_colors.set(biome_id, {r: entry.r, g: entry.g, b: entry.b})
            if (entry.name){
                const [ns, path] = biome_id.split(':')
                default_messages[`minecraft.biome.${ns}.${path.replace('/','.')}`] = entry.name
            }
        }
    }

    function handle_structure_icons(namespace: string, json: {[key: string]: {item?: string, hidden?: boolean, name?: string} | string}, ld: LoadedDimension ){
        for (const structure in json) {
            const structure_id = structure.indexOf(":") === -1 ? namespace + ":" + structure : structure

            const structure_config = json[structure]

            // legacy string handling
            if (typeof structure_config === 'string'){
                if (structure_config === 'hidden'){
                    ld.hidden_structures?.add(structure_id)
                } else {
                    ld.structure_icons?.set(structure_id, Identifier.parse(structure_config))
                }
                return 
            }
            // legacy hidden in structure file
            if (structure_config.hidden){
                ld.hidden_structures?.add(structure_id)
            // TODO: use defined texture
            // stable: set item as display
            } else if (structure_config.item){
                ld.structure_icons?.set(structure_id, Identifier.parse(structure_config.item))
            }

            if (structure_config.name){
                const [ns, path] = structure_id.split(':')
                default_messages[`minecraft.structure.${ns}.${path.replace('/','.')}`] = structure_config.name
            }
        }
    }

    async function reload() {
        await datapackStore.reloadDatapack()

        const ld: LoadedDimension = {}

        biome_colors.clear()
        ld.structure_icons = new Map<string, Identifier>()
        ld.hidden_structures = new Set()
        default_messages = {}

        // legacy biome_colors and structure_icons
        const ids = await (datapackStore.composite_datapack.getIds(ResourceLocation.DATA_FILE))
        for (const id of ids) {
            if (id.path === "biome_colors"){
                const json = await datapackStore.composite_datapack.get(ResourceLocation.DATA_FILE, id) as {[key: string]: { r: number, g: number, b: number, name?: string }}
                handle_biome_colors(id.namespace, json)
            } else if (id.path === "structure_icons"){
                const json = await datapackStore.composite_datapack.get(ResourceLocation.DATA_FILE, id) as {[key: string]: {item?: string, hidden?: boolean, name?: string} | string}
                handle_structure_icons(id.namespace, json, ld)
            }
        }

        // stable biome_colors
        const biome_colors_json = await datapackStore.composite_datapack.get(ResourceLocation.DATA_FILE, new Identifier("c", "worldgen/biome_colors")) as {[key: string]: { r: number, g: number, b: number }}
        handle_biome_colors("c", biome_colors_json)

        // stable structure_icons
        const structure_icons_json = await datapackStore.composite_datapack.get(ResourceLocation.DATA_FILE, new Identifier("c", "worldgen/structure_icons")) as {[key: string]: {item?: string, hidden?: boolean} | string}
        handle_structure_icons("c", structure_icons_json, ld)

        // stable c:hide_on_map structure tag
        for (const holder of WorldgenStructure.REGISTRY.getTagRegistry().get(new Identifier("jacobsjo", "hidden_from_map"))?.getEntries() ?? []){
            ld.hidden_structures.add(holder.key()?.toString() ?? "")
        }

        const langs = await datapackStore.composite_datapack.getIds(ResourceLocation.LANGUAGE)

        for (const locale of i18n.availableLocales){
            const minecraft_messages: {[key: string]: string} = {}
            const minecraft_locale = i18n.t('locale.minecraft_locale', 1, {locale: locale})
            for (const lang of langs.filter(l => l.path === minecraft_locale)){
                const json = await datapackStore.composite_datapack.get(ResourceLocation.LANGUAGE, lang) as any
                for (const key in json){
                    minecraft_messages[`minecraft.${key}`] = json[key]
                }
            }

            i18n.setLocaleMessage(locale, messages?.[locale] ?? {}) 
            if (locale === "en"){
                i18n.mergeLocaleMessage(locale, default_messages)
            }
            i18n.mergeLocaleMessage(locale, minecraft_messages)
        }

        var dimension_json: any
        let world_preset_dimension: any

        // A custom world preset may override a dimension that also has a direct
        // minecraft:* dimension file. Prefer the preset in that case.
        if (settingsStore.world_preset.toString() !== "minecraft:normal") {
            const world_preset_json = (await datapackStore.composite_datapack.get(ResourceLocation.WORLDGEN_WORLD_PRESET, settingsStore.world_preset)) as { dimensions: { [key: string]: any } }
            world_preset_dimension = world_preset_json.dimensions[settingsStore.dimension.toString()]
        }

        if (world_preset_dimension !== undefined) {
            dimension_json = world_preset_dimension
        } else if (await datapackStore.composite_datapack.has(ResourceLocation.DIMENSION, settingsStore.dimension)) {
            dimension_json = await datapackStore.composite_datapack.get(ResourceLocation.DIMENSION, settingsStore.dimension)
        } else {
            const world_preset_json = (await datapackStore.composite_datapack.get(ResourceLocation.WORLDGEN_WORLD_PRESET, settingsStore.world_preset)) as { dimensions: { [key: string]: any } }
            dimension_json = world_preset_json.dimensions[settingsStore.dimension.toString()]
        }

        const dimension_type_id = Identifier.parse(dimension_json.type)
        const dimension_type_json = await datapackStore.composite_datapack.get(ResourceLocation.DIMENSION_TYPE, dimension_type_id) as any
        ld.level_height = {minY: dimension_type_json.min_y, height: dimension_type_json.height}

        // get json of noise generator
        const generator = Json.readObject(dimension_json.generator) ?? {}
        if (generator?.type !== "minecraft:noise") {
            throw new Error("Dimension without noise generator")
        }

        // get Noise Settings json (from inline or from datapack)
        if (typeof generator.settings === "object") {
            ld.noise_settings_json = Json.readObject(generator.settings) ?? {}
            ld.noise_settings_id = Identifier.parse("inline:inline")
        } else if (typeof generator.settings === "string") {
            ld.noise_settings_id = Identifier.parse(Json.readString(generator.settings) ?? "")
            ld.noise_settings_json = Json.readObject(await datapackStore.composite_datapack.get(ResourceLocation.WORLDGEN_NOISE_SETTINGS, ld.noise_settings_id))
        } else {
            throw new Error("Malformed generator")
        }

        // if multi noise is using preset, fill in Build-In biome list
        ld.biome_source_json = Json.readObject(generator.biome_source) ?? {}
        if (ld.biome_source_json.type === "minecraft:multi_noise" && "preset" in ld.biome_source_json) {
            let preset = Json.readString(ld.biome_source_json.preset) ?? ""
            const preset_id = Identifier.parse(preset)
            let datapack_biomes: unknown[] | undefined

            if (await datapackStore.composite_datapack.has(ResourceLocation.WORLDGEN_MULTI_NOISE_BIOME_SOURCE_PRARAMETER_LIST, preset_id)) {
                const parameter_list = await datapackStore.composite_datapack.get(ResourceLocation.WORLDGEN_MULTI_NOISE_BIOME_SOURCE_PRARAMETER_LIST, preset_id) as { preset?: unknown, biomes?: unknown, "lithostitched:biomes"?: unknown }
                preset = Json.readString(parameter_list.preset) ?? preset

                const parameter_biomes = parameter_list["lithostitched:biomes"] ?? parameter_list.biomes
                if (Array.isArray(parameter_biomes)) {
                    datapack_biomes = parameter_biomes
                }
            }

            if (datapack_biomes === undefined) {
                const metadata = versionMetadata[settingsStore.mc_version];
                const response = await fetch(`biome_parameters/minecraft_${metadata.vanillaDatapack}/${Identifier.parse(preset).path}.json`)
                const json = await response.json()

                datapack_biomes = json.biomes
            }

            ld.biome_source_json.biomes = datapack_biomes
        }

        // structures that can actually generate in this dimension
        const possible_biomes = collectPossibleBiomes(ld.biome_source_json)
        ld.possible_biomes = possible_biomes
        ld.available_structures = new Set<string>()
        for (const id of WorldgenStructure.REGISTRY.keys()) {
            const structure = WorldgenStructure.REGISTRY.get(id)
            if (structure === undefined) continue
            if (canGenerateInDimension(structure, possible_biomes)) {
                ld.available_structures.add(id.toString())
            }
        }

        Object.assign(loaded_dimension, ld)
        biome_source = BiomeSource.fromJson(ld.biome_source_json)

    }

    const noise_generator_settings = computed(() => NoiseGeneratorSettings.fromJson(loaded_dimension.noise_settings_json))

    const random_state = computed(() => {
        return new RandomState(noise_generator_settings.value, settingsStore.seed)
    })

    const sampler = computed(() => {
        const router = (random_state.value).router
        return Climate.Sampler.fromRouter(router)
    })

    const surface_density_function = computed(() => {
        const surface_density_function_id = getCustomDensityFunction("snowcapped_surface", loaded_dimension.noise_settings_id ?? Identifier.create("empty"), settingsStore.dimension)
        if (surface_density_function_id !== undefined){
            return new DensityFunction.HolderHolder(Holder.reference(WorldgenRegistries.DENSITY_FUNCTION, surface_density_function_id)).mapAll((random_state.value).createVisitor((noise_generator_settings.value).noise, (noise_generator_settings.value).legacyRandomSource))
        } else {
            return undefined
        }
    })

    const terrain_density_function = computed(() => {
        const surface_density_function_id = getCustomDensityFunction("map_simple_terrain", loaded_dimension.noise_settings_id ?? Identifier.create("empty"), settingsStore.dimension)
        if (surface_density_function_id !== undefined){
            return new DensityFunction.HolderHolder(Holder.reference(WorldgenRegistries.DENSITY_FUNCTION, surface_density_function_id)).mapAll((random_state.value).createVisitor((noise_generator_settings.value).noise, (noise_generator_settings.value).legacyRandomSource))
        } else {
            return undefined
        }
    })    

    function getIcon(id: Identifier){
        const item = loaded_dimension.structure_icons?.get(id.toString()) ?? Identifier.create(VANILLA_ITEMS[Math.abs(hashCode(id.toString())) % VANILLA_ITEMS.length]) 
        const icon = VANILLA_ITEM_SET.has(item.path) ? item.path : FALLBACK_STRUCTURE_ICON
        return `mcicons/item/${icon}.png`
    }

    function getBiomeColor(id: string){
        var biomeColor = biome_colors.get(id)
        if (biomeColor === undefined) {
            const hash = hashCode(id)
            biomeColor = {
                r: (hash & 0xFF0000) >> 16,
                g: (hash & 0x00FF00) >> 8,
                b: hash & 0x0000FF
            }
        }
        return biomeColor
    }

    function getBiomeSource(){
        return biome_source
    }

    return { loaded_dimension, noise_generator_settings, sampler, surface_density_function, terrain_density_function, reload, getIcon, getBiomeColor, getBiomeSource }
})

